import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
  Link,
  CircularProgress,
} from "@mui/material";
import Notification from "./Notification";
import CountryRequirementsSection from "./CountryRequirementsSection";
import CountrySelectionPopup from "./CountrySelectionPopup";
import axios from "axios";

const ViewDocumentTypeDialog = ({ viewDocumentTypeDialog, setViewDocumentTypeDialog, onRequirementsUpdate }) => {
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" });
  const [formData, setFormData] = useState({
    documentTypeId: "",
    name: "",
    shortCode: "",
    description: "",
    vatRequired: false,
    edRequired: false,
    hasTimeRestriction: false,
    // New fields for country-specific requirements
    enableCountrySpecificRequirements: false,
    countryRequirements: [],
    // New fields for VAT and ED country selection
    vatRequiredCountries: [],
    edRequiredCountries: [],
  });
  const [loading, setLoading] = useState(false);
  const [countryPopupOpen, setCountryPopupOpen] = useState(false);
  const [countryPopupType, setCountryPopupType] = useState(""); // "vat" or "ed"
  const [tempSelectedCountries, setTempSelectedCountries] = useState([]); // Temporary selection in popup

  useEffect(() => {
    if (viewDocumentTypeDialog.isOpen && viewDocumentTypeDialog.params && viewDocumentTypeDialog.params.documentTypeId) {
      setFormData({
        documentTypeId: viewDocumentTypeDialog.params.documentTypeId,
        name: viewDocumentTypeDialog.params.name,
        shortCode: viewDocumentTypeDialog.params.shortCode,
        description: viewDocumentTypeDialog.params.description || "",
        vatRequired: viewDocumentTypeDialog.params.vatRequired,
        edRequired: viewDocumentTypeDialog.params.edRequired,
        hasTimeRestriction: viewDocumentTypeDialog.params.hasTimeRestriction,
        // New fields for country-specific requirements
        enableCountrySpecificRequirements: false,
        countryRequirements: [],
        // New fields for VAT and ED country selection
        vatRequiredCountries: [],
        edRequiredCountries: [],
      });

      // Load country-specific requirements if they exist
      loadCountryRequirements(viewDocumentTypeDialog.params.documentTypeId);
    }
  }, [viewDocumentTypeDialog]);

  // Load country data when dialog opens
  useEffect(() => {
    if (viewDocumentTypeDialog.isOpen && viewDocumentTypeDialog.params) {
      // Load VAT required countries
      if (viewDocumentTypeDialog.params.vatRequiredCountries) {
        const vatCountries = Array.isArray(viewDocumentTypeDialog.params.vatRequiredCountries)
          ? viewDocumentTypeDialog.params.vatRequiredCountries.map((id) => id.toString())
          : viewDocumentTypeDialog.params.vatRequiredCountries.split(",").filter((id) => id.trim());

        setFormData((prev) => ({
          ...prev,
          vatRequiredCountries: vatCountries,
        }));
      }

      // Load ED required countries
      if (viewDocumentTypeDialog.params.edRequiredCountries) {
        const edCountries = Array.isArray(viewDocumentTypeDialog.params.edRequiredCountries)
          ? viewDocumentTypeDialog.params.edRequiredCountries.map((id) => id.toString())
          : viewDocumentTypeDialog.params.edRequiredCountries.split(",").filter((id) => id.trim());

        setFormData((prev) => ({
          ...prev,
          edRequiredCountries: edCountries,
        }));
      }
    }
  }, [viewDocumentTypeDialog.isOpen, viewDocumentTypeDialog.params]);

  const loadCountryRequirements = async (documentTypeId) => {
    try {
      setLoading(true);
      // Gebruik de correcte endpoint: /api/v1/countryrequirements/documenttype/{documentTypeId}
      const response = await axios.get(`/api/v1/countryrequirements/documenttype/${documentTypeId}`);
      if (response.data && response.data.length > 0) {
        const transformedRequirements = response.data.map((req) => {
          // Transform the data to ensure compatibility
          const transformed = {
            ...req,
            id: req.id,
            countryName: req.country?.countryName || req.countryName || `Country ID: ${req.countryId}`,
          };

          // Handle backend field name HasTimRestriction and transform to frontend hasTimeRestriction
          if (req.HasTimRestriction !== undefined) {
            // Backend field name (HasTimRestriction)
            transformed.hasTimeRestriction = Boolean(req.HasTimRestriction);
          } else if (req.hasTimeRestriction !== undefined) {
            // Frontend field name (fallback)
            transformed.hasTimeRestriction = Boolean(req.hasTimeRestriction);
          } else if (req.requirementType !== undefined) {
            // Old string field (backward compatibility)
            transformed.hasTimeRestriction = req.requirementType === "hasTimeRestriction";
          } else if (req.hasTimRestriction !== undefined) {
            // Check for camelCase version
            transformed.hasTimeRestriction = Boolean(req.hasTimRestriction);
          } else if (req.HasTimeRestriction !== undefined) {
            // Check for correct spelling
            transformed.hasTimeRestriction = Boolean(req.HasTimeRestriction);
          } else if (req.timeRestriction !== undefined) {
            // Check for other variations
            transformed.hasTimeRestriction = Boolean(req.timeRestriction);
          } else if (req.TimeRestriction !== undefined) {
            // Check for other variations
            transformed.hasTimeRestriction = Boolean(req.TimeRestriction);
          } else {
            // Set to false as default
            transformed.hasTimeRestriction = false;
          }

          return transformed;
        });

        setFormData((prev) => {
          const newFormData = {
            ...prev,
            enableCountrySpecificRequirements: true,
            countryRequirements: transformedRequirements,
          };

          return newFormData;
        });
      }
    } catch (error) {
      console.error("Error loading country requirements:", error);
      // Als de API nog niet bestaat, negeer de error
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setViewDocumentTypeDialog({ ...viewDocumentTypeDialog, isOpen: false });
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]:
        name === "vatRequired" ||
        name === "edRequired" ||
        name === "hasTimeRestriction" ||
        name === "enableCountrySpecificRequirements"
          ? checked
          : value,
    });
  };

  const handleOpenCountryPopup = (type) => {
    setCountryPopupType(type);
    setCountryPopupOpen(true);
  };

  const handleCountrySelection = (selectedCountryIds) => {
    // Update form data when selection is confirmed
    // selectedCountryIds are already strings from the popup, just remove duplicates
    const uniqueSelectedIds = [...new Set(selectedCountryIds)];

    if (countryPopupType === "vat") {
      setFormData((prev) => ({
        ...prev,
        vatRequiredCountries: uniqueSelectedIds,
      }));
    } else if (countryPopupType === "ed") {
      setFormData((prev) => ({
        ...prev,
        edRequiredCountries: uniqueSelectedIds,
      }));
    }
  };

  const handleCountryConfirm = () => {
    // Selection is updated via handleCountrySelection, just close the popup
    setCountryPopupOpen(false);
  };

  const handleCountryCancel = () => {
    // Don't update form data, just close popup
    setCountryPopupOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create a completely new object with forced string conversion
      const documentTypeData = {
        name: formData.name,
        shortCode: formData.shortCode,
        description: formData.description,
        vatRequired: formData.vatRequired,
        edRequired: formData.edRequired,
        hasTimeRestriction: formData.hasTimeRestriction,
        // Force convert to strings - no matter what formData contains
        vatRequiredCountries: (() => {
          const vat = formData.vatRequiredCountries;
          if (Array.isArray(vat)) {
            return vat.join(",");
          }
          return vat || "";
        })(),
        edRequiredCountries: (() => {
          const ed = formData.edRequiredCountries;
          if (Array.isArray(ed)) {
            return ed.join(",");
          }
          return ed || "";
        })(),
      };

      try {
        await axios.put(`/api/v1/documenttypes/${formData.documentTypeId}`, documentTypeData);

        // Handle country-specific requirements update with delta logic
        try {
          // Get current requirements from database
          const currentRequirementsResponse = await axios.get(`/api/v1/countryrequirements/documenttype/${formData.documentTypeId}`);
          const currentRequirements = currentRequirementsResponse.data || [];

          // Get new requirements from form
          const newRequirements = formData.enableCountrySpecificRequirements ? formData.countryRequirements : [];

          // Find requirements to delete (in current but not in new)
          const requirementsToDelete = currentRequirements.filter(
            (currentReq) =>
              !newRequirements.some(
                (newReq) =>
                  newReq.countryId === currentReq.countryId &&
                  newReq.hasTimeRestriction === currentReq.hasTimeRestriction &&
                  newReq.specialRequirements === currentReq.specialRequirements
              )
          );

          // Find requirements to add (in new but not in current)
          const requirementsToAdd = newRequirements.filter(
            (newReq) =>
              !currentRequirements.some(
                (currentReq) =>
                  currentReq.countryId === newReq.countryId &&
                  currentReq.hasTimeRestriction === newReq.hasTimeRestriction &&
                  currentReq.specialRequirements === newReq.specialRequirements
              )
          );

          // Delete requirements that are no longer needed
          for (const reqToDelete of requirementsToDelete) {
            try {
              await axios.delete(`/api/v1/countryrequirements/${reqToDelete.id}`);
            } catch (error) {
              console.error("Error deleting country requirement:", error);
            }
          }

          // Add new requirements
          for (const reqToAdd of requirementsToAdd) {
            try {
              const requestData = {
                documentTypeId: formData.documentTypeId,
                countryId: reqToAdd.countryId,
                HasTimRestriction: reqToAdd.hasTimeRestriction,
                specialRequirements: reqToAdd.specialRequirements,
              };
              await axios.post("/api/v1/countryrequirements", requestData);
            } catch (error) {
              console.error("Error creating country requirement:", error);
            }
          }
        } catch (error) {
          console.error("Error handling country requirements update:", error);
          // If the endpoint doesn't exist yet, fall back to old logic
          if (formData.enableCountrySpecificRequirements && formData.countryRequirements.length > 0) {
            // Fallback: delete all and add new ones
            try {
              await axios.delete(`/api/v1/countryrequirements/documenttype/${formData.documentTypeId}`);
            } catch (deleteError) {
              console.log("Fallback delete failed:", deleteError);
            }

            for (const req of formData.countryRequirements) {
              try {
                const requestData = {
                  documentTypeId: formData.documentTypeId,
                  countryId: req.countryId,
                  HasTimRestriction: req.hasTimeRestriction,
                  specialRequirements: req.specialRequirements,
                };
                await axios.post("/api/v1/countryrequirements", requestData);
              } catch (addError) {
                console.error("Fallback add failed:", addError);
              }
            }
          } else {
            try {
              await axios.delete(`/api/v1/countryrequirements/documenttype/${formData.documentTypeId}`);
            } catch (deleteError) {
              console.log("Fallback delete failed:", deleteError);
            }
          }
        }

        // Update the country requirements cache
        if (onRequirementsUpdate) {
          try {
            const requirementsResponse = await axios.get(`/api/v1/countryrequirements/documenttype/${formData.documentTypeId}`);
            onRequirementsUpdate(formData.documentTypeId, requirementsResponse.data || []);
          } catch (error) {
            console.log("Error fetching updated requirements for cache:", error);
            // Set empty array if no requirements found
            onRequirementsUpdate(formData.documentTypeId, []);
          }
        }

        setNotify({
          isOpen: true,
          message: "Document type updated successfully",
          type: "success",
        });

        // Close the dialog after successful update
        handleClose();
      } catch (error) {
        setNotify({
          isOpen: true,
          message: `Error updating document type: ${error.response?.data?.message || error.message}`,
          type: "error",
        });
        setLoading(false);
        return;
      }
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "Error updating document type",
        type: "error",
      });
      setLoading(false);
    }
  };

  return (
    <Dialog open={viewDocumentTypeDialog.isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{viewDocumentTypeDialog.title}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            fullWidth
            label="Short Code"
            name="shortCode"
            value={formData.shortCode}
            onChange={handleChange}
            required
            margin="normal"
            inputProps={{ maxLength: 3 }}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
            inputProps={{ maxLength: 250 }}
          />
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
              <Switch checked={formData.vatRequired} onChange={handleChange} name="vatRequired" />
              <Typography variant="body2">VAT Required</Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  if (formData.vatRequired) {
                    handleOpenCountryPopup("vat");
                  }
                }}
                sx={{
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  padding: 0,
                  justifySelf: "start",
                  textAlign: "left",
                  opacity: formData.vatRequired ? 1 : 0.5,
                  cursor: formData.vatRequired ? "pointer" : "not-allowed",
                }}
              >
                Select Countries
              </Link>
            </Box>
            {formData.vatRequiredCountries.length > 0 && (
              <Box sx={{ ml: 4, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Selected countries: {formData.vatRequiredCountries.length}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
              <Switch checked={formData.edRequired} onChange={handleChange} name="edRequired" />
              <Typography variant="body2">ED Required</Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  if (formData.edRequired) {
                    handleOpenCountryPopup("ed");
                  }
                }}
                sx={{
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  padding: 0,
                  justifySelf: "start",
                  textAlign: "left",
                  opacity: formData.edRequired ? 1 : 0.5,
                  cursor: formData.edRequired ? "pointer" : "not-allowed",
                }}
              >
                Select Countries
              </Link>
            </Box>
            {formData.edRequiredCountries.length > 0 && (
              <Box sx={{ ml: 4, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Selected countries: {formData.edRequiredCountries.length}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
              <Switch checked={formData.hasTimeRestriction} onChange={handleChange} name="hasTimeRestriction" />
              <Typography variant="body2">Has Time Restriction</Typography>
              <Box />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
              <Switch
                checked={formData.enableCountrySpecificRequirements}
                onChange={handleChange}
                name="enableCountrySpecificRequirements"
              />
              <Typography variant="body2">Enable Country-Specific Requirements</Typography>
              <Box />
            </Box>
          </Box>

          {formData.enableCountrySpecificRequirements && (
            <CountryRequirementsSection
              requirements={formData.countryRequirements}
              onChange={(requirements) => setFormData({ ...formData, countryRequirements: requirements })}
            />
          )}

          {viewDocumentTypeDialog.usedInEntities && (
            <Typography color="warning" sx={{ mt: 2 }}>
              Warning: This document type is being used in the system and cannot be modified.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={viewDocumentTypeDialog.usedInEntities || loading}>
          Update
          {loading && (
            <CircularProgress
              size={20}
              sx={{
                color: "inherit",
                marginLeft: 1,
              }}
            />
          )}
        </Button>
      </DialogActions>

      <CountrySelectionPopup
        open={countryPopupOpen}
        onClose={handleCountryCancel}
        onConfirm={handleCountrySelection}
        title={countryPopupType === "vat" ? "Select Countries for VAT Required" : "Select Countries for ED Required"}
        selectedCountries={countryPopupType === "vat" ? formData.vatRequiredCountries : formData.edRequiredCountries}
        defaultSelectAll={false}
      />

      <Notification notify={notify} setNotify={setNotify} />
    </Dialog>
  );
};

export default ViewDocumentTypeDialog;
